import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { freelancerService, authService } from '../services/api';
import { 
  Freelancer, 
  User, 
  UserProfile, 
  Education, 
  WorkExperience, 
  Certification, 
  SocialLinks, 
  Reference,
  AvailabilitySchedule,
  TimeSlot,
  PaymentPreferences,
  GoogleProfileData
} from '../types';
import { googleAuthService } from '../services/googleAuth';
import { 
  UserPlus, 
  Mail, 
  User as UserIcon, 
  Briefcase, 
  DollarSign, 
  Clock, 
  Globe, 
  FileText, 
  X, 
  AlertCircle, 
  Lock, 
  Sparkles,
  Phone,
  MapPin,
  Calendar,
  Languages,
  GraduationCap,
  Building2,
  Award,
  Link as LinkIcon,
  Users,
  Settings,
  CreditCard,
  Plus,
  Minus
} from 'lucide-react';
import logo from '../images/RenderSpaceNT.png';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { login, updateProfile } = useAuth();
  const [userType, setUserType] = useState<'freelancer' | 'client'>('freelancer');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleProfileData, setGoogleProfileData] = useState<GoogleProfileData | null>(null);

  // Available skills and categories
  const availableSkills = [
    'AI Development', 'Machine Learning', 'Generative AI', 'Data Analytics', 'Data Visualization',
'Data Engineering', 'Digital Marketing', 'SEO', 'Social Media Marketing', 'Graphic Design',
    'UI/UX Design', 'Video Editing', 'Web Development', 'Mobile App Development', 'Copywriting',
    'Content Writing', 'Translation', 'Voice Over', '3D Modeling', 'Animation', 'Game Development',
    'DevOps', 'Cloud Computing', 'Cybersecurity', 'Blockchain', 'IoT Development'
  ];

  const availableLanguages = [
    'English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Portuguese'
  ];

  const availableIndustries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Entertainment', 'E-commerce', 'Real Estate',
    'Manufacturing', 'Transportation', 'Energy', 'Media', 'Consulting', 'Non-profit', 'Government'
  ];

  const availableTools = [
    'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java', 'C++', 'C#', 'PHP', 'Ruby',
    'Docker', 'Kubernetes', 'AWS', 'Azure', 'Google Cloud', 'Figma', 'Adobe Creative Suite',
    'Blender', 'Unity', 'Unreal Engine', 'Tableau', 'Power BI', 'TensorFlow', 'PyTorch'
  ];

  const availableSoftSkills = [
    'Communication', 'Leadership', 'Problem Solving', 'Time Management', 'Teamwork', 'Adaptability',
    'Creativity', 'Critical Thinking', 'Emotional Intelligence', 'Conflict Resolution', 'Negotiation'
  ];

  // Comprehensive form state
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    
    // Skills & Experience
    selectedSkills: [] as string[],
    experience_years: '',
    cost_per_project: '',
    bio: '',
    portfolio_url: '',
    availability: true,
    
    // Languages
    languages: [] as string[],
    
    // Education
    education: [] as Education[],
    
    // Work Experience
    work_experience: [] as WorkExperience[],
    
    // Certifications
    certifications: [] as Certification[],
    
    // Social Links
    social_links: {
      linkedin: '',
      github: '',
      twitter: '',
      website: '',
      dribbble: '',
      behance: ''
    } as SocialLinks,
    
    // Preferences
    preferred_industry: [] as string[],
    tools_technologies: [] as string[],
    soft_skills: [] as string[],
    
    // Achievements
    achievements: [] as string[],
    
    // References
    references: [] as Reference[],
    
    // Availability Schedule
    availability_schedule: {} as AvailabilitySchedule,
    
    // Payment Preferences
    payment_preferences: {
      preferred_currencies: ['USD'],
      payment_methods: ['PayPal'],
      tax_id: ''
    } as PaymentPreferences
  });

  // Helper functions
  const addEducation = () => {
    const newEducation: Education = {
      institution: '',
      degree: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      description: '',
      gpa: undefined
    };
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, newEducation]
    }));
  };

  const updateEducation = (index: number, field: keyof Education, value: any) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const addWorkExperience = () => {
    const newExperience: WorkExperience = {
      company: '',
      position: '',
      start_date: '',
      end_date: '',
      current: false,
      description: '',
      achievements: [],
      technologies_used: []
    };
    setFormData(prev => ({
      ...prev,
      work_experience: [...prev.work_experience, newExperience]
    }));
  };

  const updateWorkExperience = (index: number, field: keyof WorkExperience, value: any) => {
    setFormData(prev => ({
      ...prev,
      work_experience: prev.work_experience.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeWorkExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      work_experience: prev.work_experience.filter((_, i) => i !== index)
    }));
  };

  const addCertification = () => {
    const newCert: Certification = {
      name: '',
      issuing_organization: '',
      issue_date: '',
      expiry_date: '',
      credential_id: '',
      credential_url: ''
    };
    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, newCert]
    }));
  };

  const updateCertification = (index: number, field: keyof Certification, value: any) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addReference = () => {
    const newRef: Reference = {
      name: '',
      position: '',
      company: '',
      email: '',
      phone: '',
      relationship: ''
    };
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, newRef]
    }));
  };

  const updateReference = (index: number, field: keyof Reference, value: any) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
  };

  const removeReference = (index: number) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
  };

  const addAchievement = () => {
    setFormData(prev => ({
      ...prev,
      achievements: [...prev.achievements, '']
    }));
  };

  const updateAchievement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.map((ach, i) => 
        i === index ? value : ach
      )
    }));
  };

  const removeAchievement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };

  // Continue with existing functions...
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));

    if (name === 'email') {
      setEmailError(null);
    }
  };

  const handleSkillToggle = (skill: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedSkills.includes(skill);
      if (isSelected) {
        return {
          ...prev,
          selectedSkills: prev.selectedSkills.filter(s => s !== skill)
        };
      } else {
        if (prev.selectedSkills.length >= 15) {
          return prev;
        }
        return {
          ...prev,
          selectedSkills: [...prev.selectedSkills, skill]
        };
      }
    });
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      selectedSkills: prev.selectedSkills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleLanguageToggle = (language: string) => {
    setFormData(prev => {
      const isSelected = prev.languages.includes(language);
      if (isSelected) {
        return {
          ...prev,
          languages: prev.languages.filter(l => l !== language)
        };
      } else {
        return {
          ...prev,
          languages: [...prev.languages, language]
        };
      }
    });
  };

  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => {
      const isSelected = prev.preferred_industry.includes(industry);
      if (isSelected) {
        return {
          ...prev,
          preferred_industry: prev.preferred_industry.filter(i => i !== industry)
        };
      } else {
        return {
          ...prev,
          preferred_industry: [...prev.preferred_industry, industry]
        };
      }
    });
  };

  const handleToolToggle = (tool: string) => {
    setFormData(prev => {
      const isSelected = prev.tools_technologies.includes(tool);
      if (isSelected) {
        return {
          ...prev,
          tools_technologies: prev.tools_technologies.filter(t => t !== tool)
        };
      } else {
        return {
          ...prev,
          tools_technologies: [...prev.tools_technologies, tool]
        };
      }
    });
  };

  const handleSoftSkillToggle = (skill: string) => {
    setFormData(prev => {
      const isSelected = prev.soft_skills.includes(skill);
      if (isSelected) {
        return {
          ...prev,
          soft_skills: prev.soft_skills.filter(s => s !== skill)
        };
      } else {
        return {
          ...prev,
          soft_skills: [...prev.soft_skills, skill]
        };
      }
    });
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  // Email validation function
  const validateEmail = async (email: string) => {
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setIsCheckingEmail(true);
    try {
      const response = await authService.checkEmail(email);
      if (response.exists) {
        setEmailError('This email is already registered. Please use a different email or try logging in.');
        return false;
      }
      setEmailError(null);
      return true;
    } catch (err) {
      console.error('Error checking email:', err);
      setEmailError('Unable to verify email. Please try again.');
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Debounced email validation
  const debouncedEmailValidation = React.useCallback((email: string) => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => validateEmail(email), 500);
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, email: value }));
    setEmailError(null);
    debouncedEmailValidation(value);
  };

  // Google OAuth functions
  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError(null);
    
    try {
      await googleAuthService.initializeGoogleAuth();
      const response = await googleAuthService.signInWithGoogle();
      
      if (response.is_new_user) {
        // New user - get profile data and auto-fill form
        const profileData = await googleAuthService.getGoogleProfileData(response.google_user_info.id);
        setGoogleProfileData(profileData);
        autoFillFormWithGoogleData(profileData);
      } else {
        // Existing user - log them in
        const user: User = {
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
          userType: response.user.userType as 'freelancer' | 'client'
        };
        await login(user);
        navigate('/');
      }
    } catch (err: any) {
      console.error('Google OAuth error:', err);
      setError(err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const autoFillFormWithGoogleData = (profileData: GoogleProfileData) => {
    setFormData(prev => ({
      ...prev,
      name: profileData.name,
      email: profileData.email,
      phone: profileData.phone || '',
      bio: profileData.bio || `Professional ${profileData.name}`,
      portfolio_url: profileData.website || '',
      languages: profileData.languages,
      skills: profileData.skills.slice(0, 15), // Limit to 15 skills
      work_experience: profileData.work_experience,
      education: profileData.education,
      social_links: {
        ...prev.social_links,
        linkedin: profileData.linkedin || '',
        github: profileData.github || '',
        twitter: profileData.twitter || '',
        website: profileData.website || ''
      }
    }));
    
    // Move to step 2 to show the auto-filled data
    setCurrentStep(2);
  };

  const handleGoogleRegister = async () => {
    if (!googleProfileData) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get access token from Google
      await googleAuthService.initializeGoogleAuth();
      const response = await googleAuthService.signInWithGoogle();
      
      // Register with Google data
      const registeredUser = await googleAuthService.registerFreelancerWithGoogle(response.google_user_info.id);
      
      // Create user session
      const user: User = {
        id: registeredUser.id,
        email: registeredUser.email,
        name: registeredUser.name,
        userType: 'freelancer'
      };
      await login(user);
      navigate('/');
    } catch (err: any) {
      console.error('Google registration error:', err);
      setError(err.message || 'Google registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please try again.');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setIsLoading(false);
      return;
    }

    try {
      // Final email validation before submission
      const isEmailValid = await validateEmail(formData.email);
      if (!isEmailValid) {
        setIsLoading(false);
        return;
      }

      let registeredUser: User;

      if (userType === 'freelancer') {
        // Register as freelancer with all data
        const freelancerData = {
          email: formData.email,
          name: formData.name,
          userType: 'freelancer',
          password: formData.password,
          skills: formData.selectedSkills,
          experience_years: parseFloat(formData.experience_years) || 0,
          cost_per_project: parseFloat(formData.cost_per_project) || 0,
          bio: formData.bio,
          portfolio_url: formData.portfolio_url || undefined,
          availability: formData.availability,
          phone: formData.phone,
          languages: formData.languages,
          education: formData.education,
          work_experience: formData.work_experience,
          certifications: formData.certifications,
          social_links: formData.social_links,
          preferred_industry: formData.preferred_industry,
          tools_technologies: formData.tools_technologies,
          soft_skills: formData.soft_skills,
          achievements: formData.achievements,
          references: formData.references,
          availability_schedule: formData.availability_schedule,
          payment_preferences: formData.payment_preferences,
        };

        registeredUser = await freelancerService.register(freelancerData);

        // Save user profile
        const userProfile: UserProfile = {
          email: formData.email,
          name: formData.name,
          userType: 'freelancer',
          skills: formData.selectedSkills,
          experience_years: parseFloat(formData.experience_years) || 0,
          cost_per_project: parseFloat(formData.cost_per_project) || 0,
          bio: formData.bio,
          portfolio_url: formData.portfolio_url || undefined,
          availability: formData.availability,
          phone: formData.phone,
          languages: formData.languages,
          education: formData.education,
          work_experience: formData.work_experience,
          certifications: formData.certifications,
          social_links: formData.social_links,
          preferred_industry: formData.preferred_industry,
          tools_technologies: formData.tools_technologies,
          soft_skills: formData.soft_skills,
          achievements: formData.achievements,
          references: formData.references,
          availability_schedule: formData.availability_schedule,
          payment_preferences: formData.payment_preferences,
        };
        updateProfile(userProfile);
      } else {
        // Register as client
        registeredUser = await authService.register({
          email: formData.email,
          name: formData.name,
          userType: 'client',
          password: formData.password,
        });

        // Save client profile
        const userProfile: UserProfile = {
          email: formData.email,
          name: formData.name,
          userType: 'client',
        };
        updateProfile(userProfile);
      }

      // Create user session
      await login(registeredUser);
      navigate('/');
    } catch (err: any) {
      if (err.message === 'Email already registered') {
        setError('This email is already registered. Please use a different email or try logging in.');
      } else {
        setError('Registration failed. Please try again.');
      }
      console.error('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderSkillsExperience();
      case 3:
        return renderEducationExperience();
      case 4:
        return renderPreferences();
      case 5:
        return renderPaymentAvailability();
      default:
        return renderBasicInfo();
    }
  };

  // Step 1: Basic Information
  const renderBasicInfo = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Basic Information</h3>
        <p className="text-white/70">Tell us about yourself</p>
          </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
          <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
            Full Name *
              </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                  value={formData.name}
                  onChange={handleInputChange}
                />
            </div>

            <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
            Email Address *
              </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
            className={`w-full px-3 py-2 rounded-md bg-white/10 border ${
              emailError ? 'border-red-400' : 'border-white/20'
            } text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none`}
                  value={formData.email}
                  onChange={handleEmailChange}
                />
          {emailError && (
            <p className="text-red-400 text-sm mt-1">{emailError}</p>
                  )}
                </div>
              </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
          Phone Number
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          value={formData.phone}
          onChange={handleInputChange}
        />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
          <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
            Password *
                </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
              </div>

              <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
            Confirm Password *
                </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    minLength={6}
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                  </div>
                </div>
              </div>
  );

  // Step 2: Skills & Experience
  const renderSkillsExperience = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Skills & Experience</h3>
        <p className="text-white/70">Showcase your expertise</p>
            </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
          Skills (select up to 15)
                  </label>
                  
                  {formData.selectedSkills.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-2">
                        {formData.selectedSkills.map((skill, index) => (
                          <span
                            key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-secondary-400/20 text-secondary-400 border border-secondary-400/30"
                          >
                  <span className="truncate max-w-32">{skill}</span>
                            <button
                              type="button"
                              onClick={() => removeSkill(skill)}
                    className="ml-2 text-secondary-400 hover:text-secondary-300"
                            >
                    <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-white/50 mt-1">
              {formData.selectedSkills.length}/15 skills selected
                      </p>
                    </div>
                  )}

                    <select
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
                      onChange={(e) => {
                        if (e.target.value && !formData.selectedSkills.includes(e.target.value)) {
                          handleSkillToggle(e.target.value);
                        }
            e.target.value = '';
                      }}
          disabled={formData.selectedSkills.length >= 15}
                    >
                      <option value="">
            {formData.selectedSkills.length >= 15 
              ? 'Maximum 15 skills reached' 
                          : 'Select a skill to add...'
                        }
                      </option>
                      {availableSkills
                        .filter(skill => !formData.selectedSkills.includes(skill))
                        .map(skill => (
                          <option key={skill} value={skill}>
                            {skill}
                          </option>
                        ))
                      }
                    </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
          <label htmlFor="experience_years" className="block text-sm font-medium text-white/80 mb-2">
            Years of Experience *
                    </label>
                      <input
                        id="experience_years"
                        name="experience_years"
                        type="number"
                        min="0"
                        step="0.5"
                        required
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                        value={formData.experience_years}
                        onChange={handleInputChange}
                      />
                  </div>

                  <div>
          <label htmlFor="cost_per_project" className="block text-sm font-medium text-white/80 mb-2">
            Cost for Each Project ($) *
                    </label>
                      <input
                        id="cost_per_project"
                        name="cost_per_project"
                        type="number"
                        min="0"
                        step="0.01"
                        required
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                        value={formData.cost_per_project}
                        onChange={handleInputChange}
                      />
                  </div>
                </div>

                <div>
        <label htmlFor="bio" className="block text-sm font-medium text-white/80 mb-2">
          Bio *
                  </label>
                    <textarea
                      id="bio"
                      name="bio"
          rows={4}
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                      value={formData.bio}
                      onChange={handleInputChange}
                    />
                </div>

                <div>
        <label htmlFor="portfolio_url" className="block text-sm font-medium text-white/80 mb-2">
          Portfolio URL
                  </label>
                    <input
                      id="portfolio_url"
                      name="portfolio_url"
                      type="url"
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                      value={formData.portfolio_url}
                      onChange={handleInputChange}
                    />
                    </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">
          Languages
        </label>
        <div className="flex flex-wrap gap-2">
          {availableLanguages.map(language => (
            <button
              key={language}
              type="button"
              onClick={() => handleLanguageToggle(language)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                formData.languages.includes(language)
                  ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                  : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
              }`}
            >
              {language}
            </button>
          ))}
                  </div>
                </div>
    </div>
  );

  // Continue with other steps...

  // Step 3: Education, Work Experience, Certifications & References
  const renderEducationExperience = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Education, Work Experience, Certifications & References</h3>
        <p className="text-white/70">Build your complete professional background</p>
      </div>

      {/* Education Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">Education</h4>
          <button
            type="button"
            onClick={addEducation}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Education
          </button>
        </div>

        {formData.education.map((edu, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Education #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeEducation(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Institution"
                value={edu.institution}
                onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Degree"
                value={edu.degree}
                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Field of Study"
                value={edu.field_of_study}
                onChange={(e) => updateEducation(index, 'field_of_study', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={edu.start_date}
                onChange={(e) => updateEducation(index, 'start_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="End Date (leave empty if current)"
                value={edu.end_date || ''}
                onChange={(e) => updateEducation(index, 'end_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="number"
                step="0.01"
                min="0"
                max="4"
                placeholder="GPA (optional)"
                value={edu.gpa || ''}
                onChange={(e) => updateEducation(index, 'gpa', e.target.value ? parseFloat(e.target.value) : undefined)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
            </div>
            <textarea
              placeholder="Description (optional)"
              value={edu.description || ''}
              onChange={(e) => updateEducation(index, 'description', e.target.value)}
              rows={2}
              className="w-full mt-3 px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {/* Work Experience Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">Work Experience</h4>
          <button
            type="button"
            onClick={addWorkExperience}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Experience
          </button>
        </div>

        {formData.work_experience.map((exp, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Experience #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeWorkExperience(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Company"
                value={exp.company}
                onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Position"
                value={exp.position}
                onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={exp.start_date}
                onChange={(e) => updateWorkExperience(index, 'start_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="End Date (leave empty if current)"
                value={exp.end_date || ''}
                onChange={(e) => updateWorkExperience(index, 'end_date', e.target.value)}
                disabled={exp.current}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none disabled:opacity-50"
              />
            </div>

            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                id={`current-${index}`}
                checked={exp.current}
                onChange={(e) => updateWorkExperience(index, 'current', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor={`current-${index}`} className="text-white/80">
                I currently work here
              </label>
            </div>

            <textarea
              placeholder="Job description and responsibilities"
              value={exp.description}
              onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
              rows={3}
              className="w-full mt-3 px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {/* Certifications Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">Certifications</h4>
          <button
            type="button"
            onClick={addCertification}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Certification
          </button>
        </div>

        {formData.certifications.map((cert, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Certification #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeCertification(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Certification Name"
                value={cert.name}
                onChange={(e) => updateCertification(index, 'name', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Issuing Organization"
                value={cert.issuing_organization}
                onChange={(e) => updateCertification(index, 'issuing_organization', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={cert.issue_date}
                onChange={(e) => updateCertification(index, 'issue_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="Expiry Date (optional)"
                value={cert.expiry_date || ''}
                onChange={(e) => updateCertification(index, 'expiry_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Credential ID (optional)"
                value={cert.credential_id || ''}
                onChange={(e) => updateCertification(index, 'credential_id', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="url"
                placeholder="Credential URL (optional)"
                value={cert.credential_url || ''}
                onChange={(e) => updateCertification(index, 'credential_url', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>

      {/* References Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">Professional References</h4>
          <button
            type="button"
            onClick={addReference}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Reference
          </button>
        </div>

        {formData.references.map((ref, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Reference #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeReference(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Full Name"
                value={ref.name}
                onChange={(e) => updateReference(index, 'name', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Position"
                value={ref.position}
                onChange={(e) => updateReference(index, 'position', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Company"
                value={ref.company}
                onChange={(e) => updateReference(index, 'company', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="email"
                placeholder="Email"
                value={ref.email}
                onChange={(e) => updateReference(index, 'email', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={ref.phone || ''}
                onChange={(e) => updateReference(index, 'phone', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Relationship"
                value={ref.relationship}
                onChange={(e) => updateReference(index, 'relationship', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );



  // Step 5: Preferences
  const renderPreferences = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Work Preferences & Skills</h3>
        <p className="text-white/70">Define your ideal work environment and technical skills</p>
      </div>



      {/* Industry Preferences */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-3">
          Preferred Industries
        </label>
        <div className="flex flex-wrap gap-2">
          {availableIndustries.map(industry => (
            <button
              key={industry}
              type="button"
              onClick={() => handleIndustryToggle(industry)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                formData.preferred_industry.includes(industry)
                  ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                  : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
              }`}
            >
              {industry}
            </button>
          ))}
        </div>
      </div>

      {/* Tools & Technologies */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-3">
          Tools & Technologies
        </label>
        <div className="flex flex-wrap gap-2">
          {availableTools.map(tool => (
            <button
              key={tool}
              type="button"
              onClick={() => handleToolToggle(tool)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                formData.tools_technologies.includes(tool)
                  ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                  : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
              }`}
            >
              {tool}
            </button>
          ))}
        </div>
      </div>

      {/* Soft Skills */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-3">
          Soft Skills
        </label>
        <div className="flex flex-wrap gap-2">
          {availableSoftSkills.map(skill => (
            <button
              key={skill}
              type="button"
              onClick={() => handleSoftSkillToggle(skill)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                formData.soft_skills.includes(skill)
                  ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                  : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
              }`}
            >
              {skill}
            </button>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-white/80">
            Key Achievements
          </label>
          <button
            type="button"
            onClick={addAchievement}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Achievement
          </button>
        </div>

        {formData.achievements.map((achievement, index) => (
          <div key={index} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              placeholder="Describe a key achievement..."
              value={achievement}
              onChange={(e) => updateAchievement(index, e.target.value)}
              className="flex-1 px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => removeAchievement(index)}
              className="text-red-400 hover:text-red-300 p-2"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Social Links */}
      <div>
        <h4 className="text-lg font-medium text-white mb-3">Social Links</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="url"
            placeholder="LinkedIn URL"
            value={formData.social_links.linkedin}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, linkedin: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="GitHub URL"
            value={formData.social_links.github}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, github: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Twitter URL"
            value={formData.social_links.twitter}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, twitter: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Personal Website"
            value={formData.social_links.website}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, website: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Dribbble URL"
            value={formData.social_links.dribbble}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, dribbble: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Behance URL"
            value={formData.social_links.behance}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, behance: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );

  // Step 6: Payment & Availability
  const renderPaymentAvailability = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Payment & Availability</h3>
        <p className="text-white/70">Set your payment preferences and availability</p>
      </div>

      {/* Payment Preferences */}
      <div>
        <h4 className="text-lg font-medium text-white mb-3">Payment Preferences</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Preferred Currencies
            </label>
            <div className="flex flex-wrap gap-2">
              {['INR'].map(currency => (
                <button
                  key={currency}
                  type="button"
                  onClick={() => {
                    const isSelected = formData.payment_preferences.preferred_currencies.includes(currency);
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        ...prev.payment_preferences,
                        preferred_currencies: isSelected
                          ? prev.payment_preferences.preferred_currencies.filter(c => c !== currency)
                          : [...prev.payment_preferences.preferred_currencies, currency]
                      }
                    }));
                  }}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    formData.payment_preferences.preferred_currencies.includes(currency)
                      ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                      : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
                  }`}
                >
                  {currency}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Payment Methods
            </label>
            <div className="flex flex-wrap gap-2">
              {['PayPal', 'Stripe', 'Bank Transfer', 'Wise', 'Payoneer'].map(method => (
                <button
                  key={method}
                  type="button"
                  onClick={() => {
                    const isSelected = formData.payment_preferences.payment_methods.includes(method);
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        ...prev.payment_preferences,
                        payment_methods: isSelected
                          ? prev.payment_preferences.payment_methods.filter(m => m !== method)
                          : [...prev.payment_preferences.payment_methods, method]
                      }
                    }));
                  }}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    formData.payment_preferences.payment_methods.includes(method)
                      ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                      : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>



          <div>
            <label htmlFor="tax_id" className="block text-sm font-medium text-white/80 mb-2">
              Tax ID (optional)
            </label>
            <input
              id="tax_id"
              type="text"
              placeholder="Enter your tax identification number"
              value={formData.payment_preferences.tax_id}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                payment_preferences: {
                  ...prev.payment_preferences,
                  tax_id: e.target.value
                }
              }))}
              className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div>
        <div className="flex items-center mb-3">
                  <input
                    id="availability"
                    name="availability"
                    type="checkbox"
                    checked={formData.availability}
                    onChange={handleInputChange}
            className="h-4 w-4 text-secondary-400 focus:ring-secondary-400 border-white/30 rounded"
                  />
                  <label htmlFor="availability" className="ml-2 block text-sm text-white/80">
                    I'm currently available for new projects
                  </label>
                </div>
      </div>
    </div>
  );

  // Main render method
  return (
    <div className="min-h-screen flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="relative sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="flex justify-center">
          <div className="relative group">
            <img src={logo} alt="RenderSpace Logo" className="h-16 w-auto transition-transform group-hover:scale-105" />
            <div className="absolute -inset-1 bg-secondary-400/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </div>
        </div>
        <div className="text-center mt-6">
          <div className="flex items-center justify-center mb-2">
            <Sparkles className="text-secondary-400 mr-2" size={20} />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Create your account
            </h2>
            <Sparkles className="text-secondary-400 ml-2" size={20} />
          </div>
          <p className="text-sm text-white/70">
            Or{' '}
            <Link to="/login" className="font-medium text-secondary-400 hover:text-secondary-300 transition-colors">
              sign in to your existing account
            </Link>
          </p>
        </div>
      </div>

      <div className="relative mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="glass-card py-6 sm:py-8 px-4 sm:px-6 lg:px-10 rounded-xl border border-white/10">
          {/* User Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-white/80 mb-3">
              I am a:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setUserType('freelancer')}
                className={`p-3 sm:p-4 border rounded-lg text-center transition-all duration-300 ${
                  userType === 'freelancer'
                    ? 'border-secondary-400 glass-secondary text-secondary-400'
                    : 'border-white/20 glass text-white/70 hover:border-white/40 hover:text-white'
                }`}
              >
                <Briefcase size={20} className="mx-auto mb-2" />
                <span className="font-medium text-sm sm:text-base">Freelancer</span>
                <p className="text-xs mt-1">I want to find work</p>
              </button>
              <button
                type="button"
                onClick={() => setUserType('client')}
                className={`p-3 sm:p-4 border rounded-lg text-center transition-all duration-300 ${
                  userType === 'client'
                    ? 'border-secondary-400 glass-secondary text-secondary-400'
                    : 'border-white/20 glass text-white/70 hover:border-white/40 hover:text-white'
                }`}
              >
                <UserIcon size={20} className="mx-auto mb-2" />
                <span className="font-medium text-sm sm:text-base">Client</span>
                <p className="text-xs mt-1">I want to hire</p>
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 glass border-red-400/30 rounded-lg p-4">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Google OAuth Button */}
          {userType === 'freelancer' && (
            <div className="mb-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-white/20 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGoogleLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/60 mr-3"></div>
                    Connecting with Google...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
              
              {googleProfileData && (
                <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-sm mb-3">
                    ✓ Google profile data loaded! Review and complete your registration below.
                  </p>
                  <button
                    type="button"
                    onClick={handleGoogleRegister}
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Creating account...' : 'Complete Registration with Google'}
                  </button>
                </div>
              )}
            </div>
          )}

          {userType === 'freelancer' ? (
            <>
              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  {Array.from({ length: totalSteps }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => goToStep(i + 1)}
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all ${
                        currentStep === i + 1
                          ? 'bg-secondary-400 text-white'
                          : currentStep > i + 1
                          ? 'bg-green-500 text-white'
                          : 'bg-white/10 text-white/50 border border-white/20'
                      }`}
                    >
                      {currentStep > i + 1 ? '✓' : i + 1}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-white/60">
                  <span>Basic Info</span>
                  <span>Skills</span>
                  <span>Education & Certifications</span>
                  <span>Preferences</span>
                  <span>Payment</span>
                </div>
              </div>

              {/* Step Content */}
              <form onSubmit={handleSubmit}>
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="px-6 py-2 border border-white/20 rounded-md text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="px-6 py-2 bg-secondary-400 text-white rounded-md hover:bg-secondary-500 transition-colors"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-6 py-2 bg-secondary-400 text-white rounded-md hover:bg-secondary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Creating account...' : 'Create Account'}
                    </button>
                  )}
                </div>
              </form>
            </>
          ) : (
            /* Client Registration Form */
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-white/80">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/80">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white/80">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    minLength={6}
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-modern flex justify-center py-2 px-4 rounded-md text-sm sm:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-400 mr-2"></div>
                    Creating account...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} className="mr-2" />
                    Create Account
                  </>
                )}
              </button>
          </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 glass text-white/60 rounded-lg">Already have an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 glass border border-white/20 rounded-md text-sm sm:text-base font-medium text-white/80 hover:text-white hover:bg-white/5 transition-all duration-300"
              >
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 
